import { type ReactNode, useEffect, useRef, useState } from 'react'

interface LazyHydrateProps {
  children: ReactNode
  /** Hydrate when the element scrolls into (or near) the viewport */
  whenVisible?: boolean
  /** Hydrate on a specific DOM event (e.g. "click") */
  on?: string
  /** @deprecated No longer used. Kept for API compatibility. */
  as?: 'Fragment'
}

const CONTENTS_STYLE = { display: 'contents' as const }

/**
 * Defers hydration of server-rendered children until a trigger fires.
 *
 * The wrapper uses `display: contents` so it's invisible to layout (flex, grid, etc.).
 * Because `display: contents` removes the element's box, IntersectionObserver can't
 * observe the wrapper directly. Instead we observe the first SSR child element,
 * which retains a real bounding box.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function LazyHydrate({ children, whenVisible, on, as }: LazyHydrateProps) {
  const [hydrated, setHydrated] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (hydrated) return

    // No lazy trigger specified: hydrate immediately
    if (!whenVisible && !on) {
      setHydrated(true)
      return
    }

    const wrapper = wrapperRef.current
    if (!wrapper) return

    // If there is no SSR content to preserve (tests or client-side navigation),
    // hydrate immediately rather than waiting for a trigger that may never fire.
    if (!wrapper.hasChildNodes()) {
      setHydrated(true)
      return
    }

    if (whenVisible) {
      // Observe the first child element instead of the wrapper, because the wrapper
      // uses display:contents and has no bounding box for IntersectionObserver.
      const target = wrapper.firstElementChild
      if (!target) {
        setHydrated(true)
        return
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting) {
            setHydrated(true)
            observer.disconnect()
          }
        },
        { rootMargin: '250px' },
      )
      observer.observe(target)
      return () => observer.disconnect()
    }
  }, [hydrated, whenVisible, on])

  // Handle event-based hydration (e.g. on="click")
  useEffect(() => {
    if (!on || hydrated) return
    const el = wrapperRef.current
    if (!el) return
    const handler = () => setHydrated(true)
    el.addEventListener(on, handler)
    return () => el.removeEventListener(on, handler)
  }, [on, hydrated])

  // Server-side or already hydrated: render children normally
  if (typeof window === 'undefined' || hydrated) {
    return <div style={CONTENTS_STYLE}>{children}</div>
  }

  // Client, not yet hydrated: preserve server HTML without hydrating children.
  // dangerouslySetInnerHTML prevents React from traversing/hydrating child components,
  // while suppressHydrationWarning silences the content mismatch warning.
  // The browser keeps the server-rendered HTML visible until we switch to rendering children.
  return (
    <div ref={wrapperRef} style={CONTENTS_STYLE} suppressHydrationWarning dangerouslySetInnerHTML={{ __html: '' }} />
  )
}
